import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';

const SERVICE_LABELS = { day_program: 'Explore Program', in_home: 'IHCS', employment: 'Employment' };

export function AnnouncementDialog({ open, onOpenChange, editingAnnouncement, form, setForm, saving, onSave }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white" data-testid="announcement-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {editingAnnouncement ? 'Edit Announcement' : 'New Announcement'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label className="text-[#2D1B4E] font-medium">Title</Label>
            <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
              placeholder="e.g., Schedule change next week" className="border-[#DED8EB]" data-testid="announcement-title" />
          </div>
          <div className="space-y-2">
            <Label className="text-[#2D1B4E] font-medium">Message</Label>
            <Input value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
              placeholder="Details..." className="border-[#DED8EB]" data-testid="announcement-body" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium">Priority</Label>
              <Select value={form.priority} onValueChange={v => setForm(p => ({ ...p, priority: v }))}>
                <SelectTrigger className="border-[#DED8EB]" data-testid="announcement-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="important">Important</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium">Audience</Label>
              <Select value={form.target} onValueChange={v => setForm(p => ({ ...p, target: v }))}>
                <SelectTrigger className="border-[#DED8EB]" data-testid="announcement-target"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Everyone</SelectItem>
                  <SelectItem value="supervisors">Supervisors Only</SelectItem>
                  <SelectItem value="staff">Staff Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[#2D1B4E] font-medium">Expires <span className="text-xs text-[#8E85A0] font-normal">(optional)</span></Label>
            <Input type="date" value={form.expires_at} onChange={e => setForm(p => ({ ...p, expires_at: e.target.value }))}
              className="border-[#DED8EB]" data-testid="announcement-expires" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#DED8EB]">Cancel</Button>
          <Button onClick={onSave} disabled={saving || !form.title}
            className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="save-announcement-btn">
            {saving ? 'Saving...' : editingAnnouncement ? 'Update' : 'Post'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AccountDialog({ open, onOpenChange, editingAccount, form, setForm, saving, onSave, toggleFormService }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white" data-testid="account-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {editingAccount ? 'Edit Account' : 'Create Account'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
          <div className="space-y-2">
            <Label className="text-[#2D1B4E] font-medium">Full Name</Label>
            <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="First Last" className="border-[#DED8EB]" data-testid="account-name" />
          </div>
          {!editingAccount && (
            <>
              <div className="space-y-2">
                <Label className="text-[#2D1B4E] font-medium">Email</Label>
                <Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="email@tnsprograms.org" type="email" className="border-[#DED8EB]" data-testid="account-email" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#2D1B4E] font-medium">Password</Label>
                <Input value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="Min 8 characters" type="password" className="border-[#DED8EB]" data-testid="account-password" />
                {form.password && form.password.length < 8 && (
                  <p className="text-xs text-red-500">Password must be at least 8 characters</p>
                )}
              </div>
            </>
          )}
          <div className="space-y-2">
            <Label className="text-[#2D1B4E] font-medium">Role</Label>
            <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
              <SelectTrigger className="border-[#DED8EB]" data-testid="account-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="employee">Staff — View own schedule</SelectItem>
                <SelectItem value="scheduler">Scheduler — Edit schedules, import, print</SelectItem>
                <SelectItem value="supervisor">Supervisor — Full access</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-[#2D1B4E] font-medium">Max Weekly Hours</Label>
            <Input type="number" value={form.max_weekly_hours} onChange={e => setForm(p => ({ ...p, max_weekly_hours: parseFloat(e.target.value) || 40 }))} className="border-[#DED8EB] w-24" data-testid="account-max-hours" />
          </div>
          <div className="space-y-2">
            <Label className="text-[#2D1B4E] font-medium">Service Lines</Label>
            {Object.entries(SERVICE_LABELS).map(([key, label]) => (
              <div key={key} className="flex items-center gap-2">
                <Checkbox checked={form.service_lines.includes(key)} onCheckedChange={() => toggleFormService(key)} data-testid={`account-sl-${key}`} />
                <label className="text-sm text-[#5A4D6E] cursor-pointer">{label}</label>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#DED8EB]">Cancel</Button>
          <Button onClick={onSave} disabled={saving || !form.name || (!editingAccount && (!form.email || !form.password || form.password.length < 8))}
            className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="account-save-btn">
            {saving ? 'Saving...' : editingAccount ? 'Update' : 'Create Account'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ResetPasswordDialog({ open, onOpenChange, target, password, setPassword, saving, onSave }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm bg-white" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Reset Password
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-[#5A4D6E]">
            Set a new password for <strong>{target?.name}</strong> ({target?.email})
          </p>
          <div className="space-y-2">
            <Label className="text-[#2D1B4E] font-medium">New Password</Label>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Min 8 characters" className="border-[#DED8EB]" data-testid="reset-new-password" />
            {password && password.length < 8 && <p className="text-xs text-red-500">Must be at least 8 characters</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#DED8EB]">Cancel</Button>
          <Button onClick={onSave} disabled={saving || password.length < 8}
            className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="reset-pw-submit">
            {saving ? 'Resetting...' : 'Reset Password'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
